import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Details from "../../components/LearningTracks/Admin/MutationPages/Tabs/Details";
import Courses from "../../components/LearningTracks/Admin/MutationPages/Tabs/Courses";
import TabsNav from "../../components/tabs/Tabs";
import {
  useCreateNewLearningTrackMutation,
  useAddCourseToLearningTrackMutation,
  useUpdateLearningTrackMutation,
} from "../../services/organization/adminLearningTrack";
import Spinner from "../../components/Spinner";
import { useTranslation } from "react-i18next";

export default function AdminCreateLearningPath() {
  const TABS = {
    DETAILS: 1,
    COURSES: 2,
  };

  let navigate = useNavigate();
  const [isValidated, setIsValidated] = useState(false);
  const [submmiting, setSubmmiting] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [selectedTab, setSelectedTab] = useState(TABS.DETAILS);
  const [createNewLearningTrack] = useCreateNewLearningTrackMutation();
  const [formData, setFormData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [deletedCoursesIds, setDeltedCoursesIds] = useState([]);
  const [addCourseToLearningTrack] = useAddCourseToLearningTrackMutation();
  const [updateLearningTrack] = useUpdateLearningTrackMutation();
  const { t } = useTranslation(["common", "learningPaths"]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    async function submit() {
      try {
        setSubmmiting(true);
        const response = await createNewLearningTrack(formData);
        if (formData.image) {
          const data = new FormData();
          data.append("image", formData.image);
          await updateLearningTrack({
            learningTrackId: response.data.data.id,
            body: data,
          });
        }
        if (response.data) {
          Promise.allSettled(
            courses.map(
              async (course) =>
                await addCourseToLearningTrack({
                  learningTrackId: response.data.data.id,
                  courseId: course.id,
                }),
            ),
          );
        }
        navigate(`/admin/learning_paths`);
        if (response?.error) {
          toast.error(response?.error?.data?.message);
        } else {
          toast.success(t("successfulSave"));
        }
        setSubmmiting(false);
      } catch (e) {
        setSubmmiting(false);
        toast.error(e?.data?.message);
      }
    }
    if (!isValidated) return;
    submit();
  }, [isValidated]);

  async function onSubmit() {
    try {
      setSubmit(true);
      if (!isValidated) {
        return;
      }
      setSubmmiting(true);
      const response = await createNewLearningTrack(formData);
      if (formData.image) {
        const data = new FormData();
        data.append("image", formData.image);
        await updateLearningTrack({
          learningTrackId: response.data.data.id,
          body: data,
        });
      }
      const courseIds = courses.map((course) => course.id);

      if (response.data && courseIds.length) {
        addCourseToLearningTrack({
          learningTrackId: response.data.data.id,
          body: { course_ids: courseIds },
        });
      }

      navigate(`/admin/learning_paths`);
      if (response?.error) {
        toast.error(response?.error?.data?.message);
      } else {
        toast.success(t("successfulSave"));
      }
      setSubmmiting(false);
    } catch (e) {
      setSubmmiting(false);
      toast.error(e?.data?.message);
    }
  }

  return (
    <div className="admin-learning-path-edit">
      <div className="sticky-header header-table no-padding-b admin-learning-path-edit__header">
        <div className="admin-learning-path-edit__toolbar d-flex align-center flex-wrap">
          <div className="left-side admin-learning-path-edit__title">
            <h1 className="title-page mt-1 mb-0">
              {t("learningPaths:addNewPath")}
            </h1>
          </div>

          <div className="ms-auto right-side admin-learning-path-edit__actions">
            <ul className="list-filter">
              <li>
                <button
                  type="button"
                  className="btn btn-main-color font-15 min-w-140"
                  onClick={onSubmit}
                  disabled={submmiting}
                >
                  {t("learningPaths:savePath")}
                  {submmiting && (
                    <Spinner
                      style={{
                        marginInlineStart: "10px",
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  )}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="btn btn-grey-color font-15 px-4 min-w-140"
                  onClick={() => navigate("/admin/learning_paths")}
                >
                  {t("cancel")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <TabsNav
          activeIndex={selectedTab - 1}
          tabsConfig={[
            {
              name: "learning-track-details",
              label: t("learningPaths:pathDetails"),
              clickHandler: () => setSelectedTab(TABS.DETAILS),
            },
            {
              name: "learning-track-courses",
              label: t("learningPaths:pathCourses"),
              clickHandler: () => setSelectedTab(TABS.COURSES),
            },
          ]}
        />
      </div>

      <div className="admin-learning-path-edit__body">
        {selectedTab === TABS.DETAILS && (
          <Details
            submit={submit}
            setSubmit={setSubmit}
            setIsValidated={setIsValidated}
            formData={formData}
            setFormData={setFormData}
          />
        )}
        {selectedTab === TABS.COURSES && (
          <Courses
            courses={courses}
            setCourses={setCourses}
            deletedCoursesIds={deletedCoursesIds}
            setDeltedCoursesIds={setDeltedCoursesIds}
          />
        )}
      </div>
    </div>
  );
}
